/** Administración: catálogos, partidas y estado del almacenamiento. */

import { useEffect, useMemo, useState } from 'react';
import { Check, Database, FlaskConical, Loader2, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Boton, Campo, Chip, Tarjeta, TarjetaTitulo, Vacio } from '@/components/ui';
import { useCatalogos } from '@/hooks/useCatalogos';
import { useAuth } from '@/hooks/useAuth';
import { modoLocal } from '@/db/repositorio';
import { borrarEjemplo, cargarEjemplo, contarEjemplo } from '@/db/ejemplo';
import { COL } from '@/db/firebase';
import { actualizar, borrarGuias, marcarGuias } from '@/db/repositorio';
import { esHormigon } from '@/domain/producto';
import { revisarUnidades } from '@/domain/unidadesContrato';
import { mensajeFirestore } from '@/db/errores';
import { useGuias } from '@/hooks/useGuias';
import { uf } from '@/domain/formato';
import ImportarExcel from './ImportarExcel';
import Usuarios from './Usuarios';
import Contrato from './Contrato';
import { useDialogo } from '@/components/ui/dialogo';
import { cn } from '@/lib/cn';

type Pestana = 'partidas' | 'productos' | 'clientes' | 'destinos' | 'equipos' | 'centros';

const PESTANAS: { id: Pestana; texto: string }[] = [
  { id: 'partidas', texto: 'Partidas del contrato' },
  { id: 'productos', texto: 'Productos' },
  { id: 'clientes', texto: 'Clientes' },
  { id: 'destinos', texto: 'Destinos' },
  { id: 'equipos', texto: 'Equipos' },
  { id: 'centros', texto: 'Centros de costo' },
];

export default function Administracion() {
  const { catalogos, recargar } = useCatalogos();
  const { puede, perfil } = useAuth();
  const { guias } = useGuias();
  const [pestana, setPestana] = useState<Pestana>('partidas');
  const [q, setQ] = useState('');
  const [ejemplos, setEjemplos] = useState(0);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Cuántas van: son varias tandas y conviene que se vea que avanza. */
  const [progresoBorrado, setProgresoBorrado] = useState<string | null>(null);
  const { confirmar, pedirTexto } = useDialogo();

  useEffect(() => {
    void contarEjemplo().then(setEjemplos);
  }, []);

  async function generarEjemplo() {
    setTrabajando(true);
    setAviso(null);
    try {
      const r = await cargarEjemplo();
      setAviso(
        `Se cargaron ${r.guias} guías de los últimos 60 días, ${r.trabajadores} trabajadores y ${r.jornadas} jornadas.`,
      );
      setEjemplos(await contarEjemplo());
    } catch (e) {
      setAviso(mensajeFirestore(e, 'No se pudieron cargar los datos de ejemplo.'));
    } finally {
      setTrabajando(false);
    }
  }

  async function quitarEjemplo() {
    const si = await confirmar({
      titulo: 'Borrar los datos de ejemplo',
      mensaje:
        'Se eliminan las guías y jornadas generadas para la demostración. ' +
        'Las guías que cargaste a mano y los catálogos no se tocan.',
      aceptar: 'Borrar',
      tono: 'peligro',
    });
    if (!si) return;
    setTrabajando(true);
    const n = await borrarEjemplo();
    setAviso(`Se borraron ${n} registros de ejemplo.`);
    setEjemplos(await contarEjemplo());
    setTrabajando(false);
  }

  async function vaciarGuias() {
    const escrito = await pedirTexto({
      titulo: `Borrar las ${guias.length.toLocaleString('es-CL')} guías`,
      mensaje:
        'Se eliminan todas las guías de despacho, en todos los dispositivos. ' +
        'Los catálogos, las partidas del contrato y los usuarios no se tocan. ' +
        'No hay forma de deshacerlo: la única vuelta atrás es volver a importar el libro.',
      etiqueta: 'Escribe BORRAR para confirmar',
      marcador: 'BORRAR',
      aceptar: 'Borrar todo',
      tono: 'peligro',
    });
    if (escrito?.trim().toUpperCase() !== 'BORRAR') return;

    setTrabajando(true);
    setAviso(null);
    try {
      const n = await borrarGuias(
        guias.map((g) => g.id),
        (hechas, total) =>
          setProgresoBorrado(
            `Borrando ${hechas.toLocaleString('es-CL')} de ${total.toLocaleString('es-CL')}…`,
          ),
      );
      setProgresoBorrado(null);
      setAviso(`Se borraron ${n.toLocaleString('es-CL')} guías. Ya puedes importar el libro.`);
    } catch (e) {
      setProgresoBorrado(null);
      setAviso(mensajeFirestore(e, 'No se pudieron borrar las guías.'));
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * Guías marcadas para clasificar cuyo código sí se entiende.
   *
   * El importador las marcaba cuando el producto no estaba en el catálogo,
   * aunque el código fuera perfectamente legible. Una guía así queda fuera
   * del estado de pago, así que conviene poder arreglarlas sin recargar todo.
   */
  const porClasificarRecuperables = useMemo(
    () =>
      guias.filter((g) => g.estado === 'REQUIERE_CLASIFICACION' && esHormigon(g.productoCodigo)),
    [guias],
  );

  async function reclasificar() {
    const n = porClasificarRecuperables.length;
    const si = await confirmar({
      titulo: `Dar por buenas ${n} guías`,
      mensaje:
        'Están marcadas para clasificar porque su producto no estaba en el catálogo, pero su ' +
        'código se entiende: tienen grado y resistencia. Pasan a «emitida» y vuelven a entrar ' +
        'a los estados de pago. No se toca nada más de la guía.',
      aceptar: 'Darlas por buenas',
    });
    if (!si) return;

    setTrabajando(true);
    setAviso(null);
    try {
      await marcarGuias(
        porClasificarRecuperables.map((g) => g.id),
        { estado: 'EMITIDA' },
        perfil?.uid ?? null,
      );
      setAviso(`${n} guías quedaron como emitidas. Revisa los estados de pago del período.`);
    } catch (e) {
      setAviso(mensajeFirestore(e, 'No se pudieron reclasificar las guías.'));
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * Partidas cuya unidad no calza con lo que dicen describir.
   *
   * Se proponen, no se cambian solas: la unidad define cómo se cobra, y eso
   * no lo decide una máquina sin que nadie mire.
   */
  const unidadesSospechosas = useMemo(() => revisarUnidades(catalogos.items), [catalogos.items]);

  async function corregirUnidades() {
    const si = await confirmar({
      titulo: `Corregir ${unidadesSospechosas.length} unidades del contrato`,
      mensaje:
        unidadesSospechosas.map((u) => `${u.codigo}: ${u.actual} → ${u.sugerida}`).join(' · ') +
        '. Cambia sólo la unidad de la partida; los precios y las cantidades no se tocan. ' +
        'Conviene dejar la planilla maestra diciendo lo mismo.',
      aceptar: 'Corregir',
    });
    if (!si) return;

    setTrabajando(true);
    setAviso(null);
    try {
      for (const u of unidadesSospechosas) {
        const item = catalogos.items.find((i) => i.codigo === u.codigo);
        if (item)
          await actualizar(COL.itemsContrato, item.id, { unidad: u.sugerida }, perfil?.uid ?? null);
      }
      await recargar();
      setAviso(`${unidadesSospechosas.length} unidades corregidas.`);
    } catch (e) {
      setAviso(mensajeFirestore(e, 'No se pudieron corregir las unidades.'));
    } finally {
      setTrabajando(false);
    }
  }

  const filtro = q.trim().toLowerCase();
  const coincide = (...campos: (string | null | undefined)[]) =>
    !filtro || campos.filter(Boolean).some((c) => String(c).toLowerCase().includes(filtro));

  const sinClasificar = useMemo(
    () => catalogos.productos.filter((p) => !p.parseOk).length,
    [catalogos.productos],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Administración</h1>
          <p className="text-xs text-texto-mute">Catálogos y partidas del contrato</p>
        </div>
        <Boton
          variante="secundario"
          icono={<RefreshCw size={15} />}
          onClick={() => void recargar()}
        >
          Recargar
        </Boton>
      </div>

      <Tarjeta>
        <TarjetaTitulo
          accion={
            <Chip nivel={modoLocal ? 'AMARILLO' : 'VERDE'}>
              <Database size={12} />
              {modoLocal ? 'Almacenamiento local' : 'Firebase conectado'}
            </Chip>
          }
        >
          Origen de datos
        </TarjetaTitulo>
        <div className="p-4 text-xs text-texto-mute">
          {modoLocal ? (
            <>
              Los datos se guardan en este dispositivo. Para sincronizar entre teléfono, tablet y
              computador hay que crear un proyecto en Firebase y completar las variables del archivo{' '}
              <code className="rounded bg-petroleo-800 px-1 py-0.5 font-mono">.env</code>. Las
              instrucciones están en el README.
            </>
          ) : (
            <>
              Conectado a Firestore con caché offline. Lo que cargues sin señal se sincroniza solo
              al recuperar conexión.
            </>
          )}
        </div>
      </Tarjeta>

      {puede('administrar') && <Usuarios />}

      {puede('contrato') && <Contrato />}

      <ImportarExcel />

      {unidadesSospechosas.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo
            accion={<Chip nivel="AMARILLO">{unidadesSospechosas.length} partidas</Chip>}
          >
            Unidades que no calzan con su descripción
          </TarjetaTitulo>
          <div className="space-y-3 p-4">
            <p className="text-xs leading-relaxed text-texto-mute">
              El contrato se transcribió a mano y arrastra unidades que no corresponden. Mientras
              esas partidas se carguen a mano da lo mismo, pero si alguien intenta cargarlas por
              guía el sistema le va a exigir la unidad equivocada.
            </p>
            <div className="space-y-1.5">
              {unidadesSospechosas.map((u) => (
                <div
                  key={u.codigo}
                  className="flex flex-wrap items-center gap-2 rounded-control border border-linea bg-petroleo-800/40 px-3 py-2 text-xs"
                >
                  <span className="font-mono">{u.codigo}</span>
                  <span className="min-w-40 flex-1 truncate text-texto-mute">{u.descripcion}</span>
                  <span className="font-mono">
                    <span className="text-fluor-rojo">{u.actual}</span>
                    <span className="text-texto-mute"> → </span>
                    <span className="text-fluor-verde">{u.sugerida}</span>
                  </span>
                  <span className="w-full text-[11px] text-texto-mute sm:w-auto sm:flex-1">
                    {u.motivo}
                  </span>
                </div>
              ))}
            </div>
            <Boton
              variante="primario"
              icono={<Check size={15} />}
              onClick={() => void corregirUnidades()}
              disabled={trabajando}
            >
              Corregir las {unidadesSospechosas.length} unidades
            </Boton>
          </div>
        </Tarjeta>
      )}

      {porClasificarRecuperables.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo
            accion={<Chip nivel="AMARILLO">{porClasificarRecuperables.length} guías</Chip>}
          >
            Guías fuera del cobro por clasificar
          </TarjetaTitulo>
          <div className="space-y-3 p-4">
            <p className="text-xs leading-relaxed text-texto-mute">
              Quedaron marcadas para clasificar porque su producto no estaba en el catálogo, pero su
              código se entiende solo —tiene grado y resistencia—. Mientras sigan así{' '}
              <strong className="text-fluor-amarillo">no entran a ningún estado de pago</strong>.
            </p>
            <Boton
              variante="primario"
              icono={<Check size={15} />}
              onClick={() => void reclasificar()}
              disabled={trabajando}
            >
              Dar por buenas las {porClasificarRecuperables.length} guías
            </Boton>
          </div>
        </Tarjeta>
      )}

      {puede('administrar') && (
        <Tarjeta>
          <TarjetaTitulo
            accion={<Chip nivel="NEUTRO">{guias.length.toLocaleString('es-CL')} guías</Chip>}
          >
            Empezar de cero
          </TarjetaTitulo>
          <div className="space-y-3 p-4">
            <p className="text-xs leading-relaxed text-texto-mute">
              Borra todas las guías para volver a importarlas desde el libro. Sirve cuando la carga
              anterior quedó mal o cambió el archivo de origen. Los catálogos, las partidas del
              contrato y los usuarios quedan como están.
            </p>
            {guias.length === 0 ? (
              // Un botón apagado sin explicación se lee como una falla.
              <p className="text-xs text-texto-mute">
                No hay guías cargadas, así que no hay nada que borrar.
              </p>
            ) : (
              <Boton
                variante="peligro"
                icono={<Trash2 size={15} />}
                onClick={() => void vaciarGuias()}
                disabled={trabajando}
              >
                {progresoBorrado ??
                  (trabajando
                    ? 'Borrando…'
                    : `Borrar las ${guias.length.toLocaleString('es-CL')} guías`)}
              </Boton>
            )}
          </div>
        </Tarjeta>
      )}

      <Tarjeta>
        <TarjetaTitulo
          accion={
            ejemplos > 0 ? <Chip nivel="AMARILLO">{ejemplos} registros de ejemplo</Chip> : undefined
          }
        >
          Datos de ejemplo
        </TarjetaTitulo>
        <div className="space-y-3 p-4">
          <p className="text-xs text-texto-mute">
            Genera guías de los últimos 60 días y jornadas de un mes, con la mezcla real de
            productos, clientes y plantas, para ver el resumen, los gráficos y el estado de pago
            funcionando antes de cargar datos de verdad. Quedan marcados aparte, así que se pueden
            borrar sin tocar lo que cargues a mano.
          </p>
          <div className="flex flex-wrap gap-2">
            <Boton
              variante="secundario"
              icono={
                trabajando ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <FlaskConical size={15} />
                )
              }
              onClick={() => void generarEjemplo()}
              disabled={trabajando}
            >
              Cargar datos de ejemplo
            </Boton>
            {ejemplos > 0 && (
              <Boton
                variante="peligro"
                icono={<Trash2 size={15} />}
                onClick={() => void quitarEjemplo()}
                disabled={trabajando}
              >
                Borrar los de ejemplo
              </Boton>
            )}
          </div>
          {aviso && <p className="text-xs text-fluor-verde">{aviso}</p>}
        </div>
      </Tarjeta>

      {sinClasificar > 0 && (
        <Tarjeta>
          <TarjetaTitulo>Productos por clasificar</TarjetaTitulo>
          <div className="flex flex-wrap gap-1.5 p-4">
            {catalogos.productos
              .filter((p) => !p.parseOk)
              .map((p) => (
                <Chip key={p.id} nivel="AMARILLO">
                  {p.codigo}
                </Chip>
              ))}
          </div>
          <p className="px-4 pb-4 text-xs text-texto-mute">
            Estos códigos no siguen la nomenclatura estándar, así que no se les aplican recargos
            automáticos. Hay que asignarles partida a mano en cada guía.
          </p>
        </Tarjeta>
      )}

      <Tarjeta>
        <div className="flex flex-wrap gap-1 border-b border-linea p-2">
          {PESTANAS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPestana(p.id)}
              className={cn(
                'rounded-control px-3 py-1.5 text-xs font-medium transition-colors',
                pestana === p.id
                  ? 'bg-petroleo-600 text-texto'
                  : 'text-texto-mute hover:bg-white/5 hover:text-texto',
              )}
            >
              {p.texto}
            </button>
          ))}
        </div>

        <div className="p-4">
          <Campo
            placeholder="Buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sufijo={<Search size={14} />}
          />
        </div>

        <div className="max-h-[32rem] overflow-y-auto">
          {pestana === 'partidas' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Descripción</th>
                  <th className="px-3 py-2 font-medium">Unidad</th>
                  <th className="px-3 py-2 text-right font-medium">Base UF</th>
                  <th className="px-3 py-2 text-right font-medium">Vigente UF</th>
                </tr>
              </thead>
              <tbody>
                {catalogos.items
                  .filter((i) => coincide(i.codigo, i.descripcion))
                  .map((i) => (
                    <tr key={i.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5 font-mono tabular-nums">{i.codigo}</td>
                      <td className="max-w-[22rem] truncate px-3 py-1.5">{i.descripcion}</td>
                      <td className="px-3 py-1.5 text-texto-mute">{i.unidad}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-texto-mute">
                        {uf(i.precioBaseUf)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {uf(i.precioUf)}
                        {!i.aplicaReajuste && (
                          <span className="ml-1 text-[10px] text-texto-mute">sin reajuste</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {pestana === 'productos' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Familia</th>
                  <th className="px-3 py-2 font-medium">Grado</th>
                  <th className="px-3 py-2 text-right font-medium">MPa</th>
                  <th className="px-3 py-2 text-right font-medium">Cono</th>
                  <th className="px-3 py-2 font-medium">Extras</th>
                  <th className="px-3 py-2 font-medium">Alias</th>
                </tr>
              </thead>
              <tbody>
                {catalogos.productos
                  .filter((p) => coincide(p.codigo, ...p.alias))
                  .map((p) => (
                    <tr key={p.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5 font-mono">
                        {p.codigo}
                        {!p.parseOk && <span className="ml-1.5 text-fluor-amarillo">•</span>}
                      </td>
                      <td className="px-3 py-1.5 text-texto-mute">{p.familia}</td>
                      <td className="px-3 py-1.5">{p.grado ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {p.mpa ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {p.cono ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-fluor-amarillo">
                        {p.extras.join(' ') || '—'}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-1.5 text-texto-mute">
                        {p.alias.join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {(['clientes', 'destinos', 'equipos', 'centros'] as const).includes(
            pestana as 'clientes',
          ) && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Alias agrupados</th>
                </tr>
              </thead>
              <tbody>
                {(pestana === 'clientes'
                  ? catalogos.clientes
                  : pestana === 'destinos'
                    ? catalogos.destinos
                    : pestana === 'equipos'
                      ? catalogos.equipos
                      : catalogos.centrosCosto
                )
                  .filter((c) => coincide(c.nombre, ...c.alias))
                  .map((c) => (
                    <tr key={c.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5">{c.nombre}</td>
                      <td className="px-3 py-1.5 text-texto-mute">
                        {c.alias.length ? c.alias.join(' · ') : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {catalogos.items.length === 0 && <Vacio titulo="Sin datos" />}
        </div>
      </Tarjeta>
    </div>
  );
}
